import fnsFormat from 'date-fns/format'
import { deAT } from 'date-fns/locale'
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks
} from 'date-fns'

/* eslint-disable no-undef */
export enum DatePeriod {
  LAST_7_DAYS = 'LAST_7_DAYS',
  LAST_30_DAYS = 'LAST_30_DAYS',
  LAST_90_DAYS_LAST_TRIP = 'LAST_90_DAYS_LAST_TRIP',
  LAST_WEEK = 'LAST_WEEK',
  LAST_MONTH = 'LAST_MONTH',
  LAST_3_MONTHS = 'LAST_3_MONTHS',
  CUSTOM = 'CUSTOM',
}

const periodTextMapping = {
  LAST_7_DAYS: 'Letzten 7 Tage',
  LAST_30_DAYS: 'Letzten 30 Tage',
  LAST_90_DAYS_LAST_TRIP: 'Letzten 90 Tage ab letzter Fahrt',
  LAST_WEEK: 'Letzte Woche ({})',
  LAST_MONTH: 'Letzter Monat ({})',
  LAST_3_MONTHS: 'Letzten 3 Monate ({})',
  CUSTOM: 'Bestimmter Zeitraum',
}

const locale = { locale: deAT }

export function textFromDatePeriod (datePeriod: DatePeriod, lastTripDate: Date) {
  switch (datePeriod) {
    case DatePeriod.LAST_WEEK:
      return periodTextMapping[datePeriod].replace('{}', getDatePeriodFormattedString(datePeriod, lastTripDate, 'dd.MM.'))
    case DatePeriod.LAST_MONTH:
      const monthString = fnsFormat(calculateFromAndUntilDatesReturnsDates(datePeriod, lastTripDate).from, 'MMMM', locale)
      return periodTextMapping[datePeriod].replace('{}', monthString)
    case DatePeriod.LAST_3_MONTHS:
      return periodTextMapping[datePeriod].replace('{}', getDatePeriodFormattedString(datePeriod, lastTripDate, 'MMM'))
    default:
      return periodTextMapping[datePeriod]
  }
}

export function calculateFromAndUntilDates (datePeriod: DatePeriod, lastTripDate: Date): { from: string, until: string } {
  const dates = calculateFromAndUntilDatesReturnsDates(datePeriod, lastTripDate)
  return { from: dates.from.toISOString(), until: dates.until.toISOString() }
}

export function isoDateToDateString (date: string) {
  return new Date(date).toLocaleDateString('sv-SE')
}

function getDatePeriodFormattedString (datePeriod: DatePeriod, lastTripDate: Date, format: string) {
  const dates = calculateFromAndUntilDatesReturnsDates(datePeriod, lastTripDate)
  return `${fnsFormat(dates.from, format, locale)} - ${fnsFormat(dates.until, format, locale)}`
}

function calculateFromAndUntilDatesReturnsDates (datePeriod: DatePeriod, lastTripDate: Date): { from: Date, until: Date } {
  let from = startOfDay(new Date())
  let until = endOfDay(new Date())

  // below: always one day less because startOfDay and endOfDay

  switch (datePeriod) {
    case DatePeriod.LAST_7_DAYS:
      from = subDays(from, 6)
      break
    case DatePeriod.LAST_30_DAYS:
      from = subDays(from, 29)
      break
    case DatePeriod.LAST_90_DAYS_LAST_TRIP:
      from = subDays(startOfDay(lastTripDate), 89)
      until = endOfDay(lastTripDate)
      break
    case DatePeriod.LAST_WEEK:
      from = startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 })
      until = endOfWeek(from, { weekStartsOn: 1 })
      break
    case DatePeriod.LAST_MONTH:
      from = startOfMonth(subMonths(from, 1))
      until = endOfMonth(from)
      break
    case DatePeriod.LAST_3_MONTHS:
      from = startOfMonth(subMonths(from, 3))
      until = endOfMonth(subMonths(until, 1))
      break
    default:
      throw Error(`Cannot calculate dates from period ${datePeriod}`)
  }

  return { from, until }
}
